import { useCallback, useEffect, useRef, useState } from 'react';

import { getAuthState } from '@confluence/loom-utils/entry-points/getAuthState.ts';
import { cfetch } from '@confluence/network';

import {
	EXTERNAL_PRODUCT_CONNECTION,
	type ExternalProductConnection,
} from '../__types__/RemoteObject.ts';

type AuthenticationStatus = {
	isAuthenticated: boolean;
	error?: Error;
};

export type ThirdPartyAuthenticationResult = {
	authentications: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	outboundAuthUrls: Partial<Record<ExternalProductConnection, string>>;
	isLoading: boolean;
	refetch: () => Promise<void>;
};

// name` values from `search.configuration.thirdPartyProducts
enum SEARCH_THIRD_PARTY_CONNECTOR_NAME {
	GOOGLE_DRIVE = 'google-drive-connector',
	MICROSOFT_TEAMS = 'microsoft-teams-connector',
}

type SearchThirdPartyProductRow = {
	name: string;
	isUserOAuthed: boolean;
	outboundAuthUrl: string;
	providerId?: string | null;
};

type SearchThirdPartyAuthConfigurationResponse = {
	data?: {
		search?: {
			configuration?: {
				thirdPartyProducts?: SearchThirdPartyProductRow[];
			} | null;
		} | null;
	};
	errors?: ReadonlyArray<{ message?: string }>;
};

const SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_OPERATION = 'SearchThirdPartyAuthConfiguration';

const SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_QUERY = `
query SearchThirdPartyAuthConfiguration($cloudId: ID!) {
	search {
		configuration(cloudId: $cloudId) {
			thirdPartyProducts {
				name
				isUserOAuthed
				outboundAuthUrl
				providerId
			}
		}
	}
}
`.trim();

const getLoomAuthStatus = async (): Promise<AuthenticationStatus> => {
	try {
		const loomUserStatus = await getAuthState();

		return {
			isAuthenticated: loomUserStatus !== undefined && loomUserStatus !== 'NOT_FOUND',
		};
	} catch (e) {
		return {
			isAuthenticated: false,
			error: e instanceof Error ? e : new Error('Failed to fetch Loom auth status'),
		};
	}
};

async function fetchSearchThirdPartyProducts(
	cloudId: string,
): Promise<{ products: SearchThirdPartyProductRow[] } | { error: Error }> {
	try {
		const response = await cfetch(
			`/gateway/api/graphql?q=${SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_OPERATION}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					query: SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_QUERY,
					variables: { cloudId },
					operationName: SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_OPERATION,
				}),
			},
		);

		const json = (await response.json()) as SearchThirdPartyAuthConfigurationResponse;

		if (json.errors?.length) {
			return {
				error: new Error(json.errors.map((e) => e.message ?? 'GraphQL error').join('; ')),
			};
		}

		const products = json.data?.search?.configuration?.thirdPartyProducts ?? [];
		return { products };
	} catch (e) {
		return {
			error: e instanceof Error ? e : new Error('Failed to fetch search third-party configuration'),
		};
	}
}

function authStatusForSearchConnector(
	products: SearchThirdPartyProductRow[],
	connectorName: string,
	fetchError?: Error,
): AuthenticationStatus {
	if (fetchError) {
		return { isAuthenticated: false, error: fetchError };
	}

	const matches = products.filter((p) => p.name === connectorName);
	if (matches.length === 0) {
		return { isAuthenticated: false };
	}

	return {
		isAuthenticated: matches.some((p) => p.isUserOAuthed),
	};
}

function outboundAuthUrlForConnector(
	products: SearchThirdPartyProductRow[],
	connectorName: string,
): string | undefined {
	const match = products.find((p) => p.name === connectorName);
	return match?.outboundAuthUrl;
}

type UseFetch3PAuthenticationsProps = {
	products: ExternalProductConnection[];
	cloudId: string;
};

export const useFetch3PAuthentications = ({
	products,
	cloudId,
}: UseFetch3PAuthenticationsProps): ThirdPartyAuthenticationResult => {
	const [authentications, setAuthentications] = useState<
		ThirdPartyAuthenticationResult['authentications']
	>({});
	const [outboundAuthUrls, setOutboundAuthUrls] = useState<
		ThirdPartyAuthenticationResult['outboundAuthUrls']
	>({});
	const [isLoading, setIsLoading] = useState(false);

	const productsKey = products.join(',');
	const stableProductsRef = useRef(products);
	if (stableProductsRef.current.join(',') !== productsKey) {
		stableProductsRef.current = products;
	}

	const refetch = useCallback(async () => {
		const products = stableProductsRef.current;
		if (products.length === 0) {
			setAuthentications({});
			setOutboundAuthUrls({});
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		try {
			const results: ThirdPartyAuthenticationResult['authentications'] = {};

			let searchProducts: SearchThirdPartyProductRow[] = [];
			let searchFetchError: Error | undefined;

			if (
				products.includes(EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS) ||
				products.includes(EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE)
			) {
				const searchResult = await fetchSearchThirdPartyProducts(cloudId);
				if ('error' in searchResult) {
					searchFetchError = searchResult.error;
				} else {
					searchProducts = searchResult.products;
				}
			}

			for (const product of products) {
				switch (product) {
					case EXTERNAL_PRODUCT_CONNECTION.LOOM:
						results[EXTERNAL_PRODUCT_CONNECTION.LOOM] = await getLoomAuthStatus();
						break;
					case EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS:
						results[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS] = authStatusForSearchConnector(
							searchProducts,
							SEARCH_THIRD_PARTY_CONNECTOR_NAME.MICROSOFT_TEAMS,
							searchFetchError,
						);
						break;
					case EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE:
						results[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE] = authStatusForSearchConnector(
							searchProducts,
							SEARCH_THIRD_PARTY_CONNECTOR_NAME.GOOGLE_DRIVE,
							searchFetchError,
						);
						break;
				}
			}

			const outboundUrls: ThirdPartyAuthenticationResult['outboundAuthUrls'] = {};
			if (products.includes(EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS)) {
				const url = outboundAuthUrlForConnector(
					searchProducts,
					SEARCH_THIRD_PARTY_CONNECTOR_NAME.MICROSOFT_TEAMS,
				);
				if (url) {
					outboundUrls[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS] = url;
				}
			}
			if (products.includes(EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE)) {
				const url = outboundAuthUrlForConnector(
					searchProducts,
					SEARCH_THIRD_PARTY_CONNECTOR_NAME.GOOGLE_DRIVE,
				);
				if (url) {
					outboundUrls[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE] = url;
				}
			}

			setAuthentications(results);
			setOutboundAuthUrls(outboundUrls);
		} finally {
			setIsLoading(false);
		}
	}, [cloudId]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void refetch();
	}, [refetch]);

	return { authentications, outboundAuthUrls, isLoading, refetch };
};
